import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { supabase } from '../../../lib/supabase';


const PartnerRegistrationForm = ({ onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    businessType: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
    password: '',
    confirmPassword: '',
    serviceCategories: [],
    taxId: '',
    licenseNumber: '',
    agreeToTerms: false,
    agreeToPrivacy: false
  });

  const [formErrors, setFormErrors] = useState({});

  const businessTypes = [
    { value: 'airline', label: 'Airline' },
    { value: 'hotel', label: 'Hotel/Accommodation' },
    { value: 'car_rental', label: 'Car Rental' },
    { value: 'travel_agency', label: 'Travel Agency' },
    { value: 'tour_operator', label: 'Tour Operator' },
    { value: 'insurance', label: 'Travel Insurance' },
    { value: 'transportation', label: 'Ground Transportation' },
    { value: 'other', label: 'Other Services' }
  ];

  const serviceCategoryOptions = [
    { value: 'domestic_flights', label: 'Domestic Flights' },
    { value: 'international_flights', label: 'International Flights' },
    { value: 'accommodations', label: 'Accommodations' },
    { value: 'ground_transport', label: 'Ground Transportation' },
    { value: 'travel_insurance', label: 'Travel Insurance' },
    { value: 'visa_services', label: 'Visa Services' },
    { value: 'concierge', label: 'Concierge Services' },
    { value: 'emergency_support', label: 'Emergency Support' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (formErrors?.[field]) {
      setFormErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData?.companyName?.trim()) errors.companyName = 'Company name is required';
    if (!formData?.businessType) errors.businessType = 'Business type is required';
    if (!formData?.contactName?.trim()) errors.contactName = 'Contact name is required';
    if (!formData?.contactEmail?.trim()) {
      errors.contactEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.contactEmail)) {
      errors.contactEmail = 'Invalid email format';
    }
    if (!formData?.contactPhone?.trim()) errors.contactPhone = 'Phone number is required';
    if (!formData?.password) {
      errors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    if (formData?.password !== formData?.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    if (formData?.serviceCategories?.length === 0) {
      errors.serviceCategories = 'Select at least one service category';
    }
    if (!formData?.agreeToTerms) errors.agreeToTerms = 'You must agree to the terms';
    if (!formData?.agreeToPrivacy) errors.agreeToPrivacy = 'You must agree to the privacy policy';

    setFormErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);

    if (!validateForm()) {
      setError('Please fix the errors in the form');
      return;
    }

    setLoading(true);

    try {
      // Create auth user
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email: formData?.contactEmail,
        password: formData?.password,
        options: {
          data: {
            full_name: formData?.contactName,
            phone: formData?.contactPhone,
            role: 'partner',
            company_name: formData?.companyName
          }
        }
      });

      if (authError) throw authError;

      // Create partner record
      const { error: partnerError } = await supabase?.from('partners')?.insert({
        user_id: authData?.user?.id,
        company_name: formData?.companyName,
        business_type: formData?.businessType,
        contact_name: formData?.contactName,
        contact_email: formData?.contactEmail,
        contact_phone: formData?.contactPhone,
        website: formData?.website || null,
        service_categories: formData?.serviceCategories,
        tax_id: formData?.taxId || null,
        license_number: formData?.licenseNumber || null,
        status: 'pending_approval',
        compliance_verified: false
      });

      if (partnerError) throw partnerError;

      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
      }, 2000);
    } catch (err) {
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="glass-container rounded-2xl p-8 text-center space-y-4">
        <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto">
          <Icon name="CheckCircle" size={32} className="text-success" />
        </div>
        <h3 className="text-2xl font-bold text-foreground">Registration Successful!</h3>
        <p className="text-muted-foreground">
          Your partner application has been submitted. Please check your email to verify your account.
        </p>
        <p className="text-sm text-muted-foreground">
          Redirecting to application submission...
        </p>
      </div>
    );
  }

  return (
    <div className="glass-container rounded-2xl p-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <h2 className="text-3xl font-bold text-foreground">Travel Partner Registration</h2>
          <p className="text-muted-foreground">
            Register your organization to join our trusted partner network.
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-error mt-0.5" />
            <p className="text-sm text-error">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Company Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Company Information</h3>
            
            <Input
              label="Company Name"
              required
              value={formData?.companyName}
              onChange={(e) => handleChange('companyName', e?.target?.value)}
              error={formErrors?.companyName}
              placeholder="Acme Travel Services"
            />

            <div className="grid md:grid-cols-2 gap-4">
              <Select
                label="Business Type"
                required
                options={businessTypes}
                value={formData?.businessType}
                onChange={(value) => handleChange('businessType', value)}
                error={formErrors?.businessType}
                placeholder="Select business type"
              />

              <Input
                label="Website"
                type="url"
                value={formData?.website}
                onChange={(e) => handleChange('website', e?.target?.value)}
                placeholder="https://www.example.com"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Input
                label="Tax ID / EIN"
                value={formData?.taxId}
                onChange={(e) => handleChange('taxId', e?.target?.value)}
                placeholder="12-3456789"
                description="Optional"
              />

              <Input
                label="License Number"
                value={formData?.licenseNumber}
                onChange={(e) => handleChange('licenseNumber', e?.target?.value)}
                placeholder="LIC-123456"
                description="Optional"
              />
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Primary Contact Information</h3>
            
            <Input
              label="Contact Name"
              required
              value={formData?.contactName}
              onChange={(e) => handleChange('contactName', e?.target?.value)}
              error={formErrors?.contactName}
              placeholder="Jane Smith"
            />

            <div className="grid md:grid-cols-2 gap-4">
              <Input
                label="Contact Email"
                type="email"
                required
                value={formData?.contactEmail}
                onChange={(e) => handleChange('contactEmail', e?.target?.value)}
                error={formErrors?.contactEmail}
                placeholder="jane.smith@example.com"
              />

              <Input
                label="Contact Phone"
                type="tel"
                required
                value={formData?.contactPhone}
                onChange={(e) => handleChange('contactPhone', e?.target?.value)}
                error={formErrors?.contactPhone}
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Input
                label="Password"
                type="password"
                required
                value={formData?.password}
                onChange={(e) => handleChange('password', e?.target?.value)}
                error={formErrors?.password}
                description="Minimum 8 characters"
              />

              <Input
                label="Confirm Password"
                type="password"
                required
                value={formData?.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e?.target?.value)}
                error={formErrors?.confirmPassword}
              />
            </div>
          </div>

          {/* Service Capabilities */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Service Capabilities</h3>
            
            <Select
              label="Service Categories"
              required
              multiple
              searchable
              options={serviceCategoryOptions}
              value={formData?.serviceCategories}
              onChange={(value) => handleChange('serviceCategories', value)}
              error={formErrors?.serviceCategories}
              description="Select all services your organization provides"
              placeholder="Select service categories"
            />
          </div>

          {/* Terms and Conditions */}
          <div className="space-y-3">
            <Checkbox
              checked={formData?.agreeToTerms}
              onChange={(e) => handleChange('agreeToTerms', e?.target?.checked)}
              label="I agree to the Terms of Service and Partner Agreement"
              error={formErrors?.agreeToTerms}
              required
            />

            <Checkbox
              checked={formData?.agreeToPrivacy}
              onChange={(e) => handleChange('agreeToPrivacy', e?.target?.checked)}
              label="I agree to the Privacy Policy and GDPR compliance requirements"
              error={formErrors?.agreeToPrivacy}
              required
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            fullWidth
            loading={loading}
            iconName="Building2"
          >
            Complete Registration
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PartnerRegistrationForm;